import { useEffect, useRef, useState } from "react";
import { t } from "../i18n";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Camera, Contact, ChevronRight, X, CheckCircle2 } from "lucide-react";
import { scrollTop } from "../components/ScrollToTop";
import { toast } from "sonner";
import { api, errorMessage } from "../lib/api";
import { Shell } from "../components/Shell";
import { Sheet } from "../components/Sheet";
import { Avatar } from "../components/Avatar";
import { CountrySelect } from "../components/CountrySelect";
import { IDIOMAS, ESTADOS, mesNombre, idiomaLabel } from "../lib/data";

const EMPTY = {
  nombre: "", apodo: "", pais_iso: "", idiomas: [], ciudad_vive: { nombre: "", pais: "" },
  cumple: { dia: "", mes: "", anio: "" }, conocidos: { fecha: "", lugar: "", como: "" }, estado: "activa",
  etiquetas: [], notas: { gustos: "", alergias: "", familia: "", trabajo: "", otro: "" }, telefono: "", redes: "", dias_sin_contacto_alerta: "",
};

const Section = ({ title, children }) => (
  <section className="surface p-4 space-y-3">
    <h2 className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">{title}</h2>
    {children}
  </section>
);

const Input = ({ label, testId, ...props }) => (
  <div>
    {label && <label className="label">{label}</label>}
    <input data-testid={testId} className="field" {...props} />
  </div>
);

export default function PersonaForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [tag, setTag] = useState("");
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();
  const [banner, setBanner] = useState("");
  const vcfRef = useRef();
  const [importSheet, setImportSheet] = useState(false);
  const [importing, setImporting] = useState(false);
  const location = useLocation();
  const importado = location.state?.importado;
  useEffect(() => {
    if (!importado && !location.state?.aviso) return;
    scrollTop();
    const id2 = setTimeout(() => {
      scrollTop();
      const pendiente = [...document.querySelectorAll('[data-testid="person-form"] input:not([type=file]):not([type=hidden])')].find((el) => !el.value);
      pendiente?.focus({ preventScroll: true });
      scrollTop();
    }, 350);
    return () => clearTimeout(id2);
  }, [importado, location.state?.aviso]);
  const autoImport = !id && new URLSearchParams(location.search).get("importar") === "1";

  const hasPicker = typeof navigator !== "undefined" && "contacts" in navigator && "ContactsManager" in window && typeof navigator.contacts?.select === "function";
  const importar = () => setImportSheet(true);
  const pickContact = async () => {
    try {
      const [c] = await navigator.contacts.select(["name", "tel", "email", "icon", "address"], { multiple: false });
      if (!c) return;
      setForm((f) => ({ ...f, nombre: c.name?.[0] || f.nombre, telefono: c.tel?.[0] || f.telefono, redes: c.email?.[0] || f.redes,
        ciudad_vive: { nombre: c.address?.[0]?.city || f.ciudad_vive.nombre, pais: c.address?.[0]?.country || f.ciudad_vive.pais } }));
      if (c.icon?.[0]) { setFile(c.icon[0]); setPreview(URL.createObjectURL(c.icon[0])); }
      setImportSheet(false);
      setBanner(c.name?.[0] || "");
      scrollTop();
      toast.success(t("person.importedToast"));
    } catch (e) { if (e?.name !== "AbortError") toast.error(t("person.importFail")); }
  };
  const onVcf = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/import/vcf", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("person.imported"));
      navigate(`/personas/${data.id}/editar`, { replace: true, state: { importado: data.nombre } });
    } catch (err) { toast.error(errorMessage(err)); } finally { setImporting(false); setImportSheet(false); }
  };

  useEffect(() => { if (autoImport) setImportSheet(true); }, [autoImport]);

  useEffect(() => {
    if (!id) return;
    api.get(`/people/${id}`).then(({ data }) => {
      setForm({
        ...EMPTY, ...data,
        pais_iso: data.pais_iso || "", apodo: data.apodo || "", telefono: data.telefono || "", redes: data.redes || "",
        dias_sin_contacto_alerta: data.dias_sin_contacto_alerta ?? "",
        ciudad_vive: { nombre: data.ciudad_vive?.nombre || "", pais: data.ciudad_vive?.pais || "" },
        cumple: { dia: data.cumple?.dia ?? "", mes: data.cumple?.mes ?? "", anio: data.cumple?.anio ?? "" },
        conocidos: { fecha: data.conocidos?.fecha || "", lugar: data.conocidos?.lugar || "", como: data.conocidos?.como || "" },
        notas: Object.fromEntries(Object.keys(EMPTY.notas).map((k) => [k, data.notas?.[k] || ""])),
      });
      setPreview(data.foto_url);
    }).catch(() => navigate("/personas"));
  }, [id, navigate]);

  const set = (path, value) => setForm((f) => {
    const [a, b] = path.split(".");
    return b ? { ...f, [a]: { ...f[a], [b]: value } } : { ...f, [a]: value };
  });
  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f); setPreview(URL.createObjectURL(f));
  };
  const addTag = () => {
    const tg = tag.trim().toLowerCase();
    if (t && !form.etiquetas.includes(t)) set("etiquetas", [...form.etiquetas, t]);
    setTag("");
  };
  const num = (v) => (v === "" || v == null ? null : Number(v));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim()) return toast.error(t("person.nameRequired"));
    setSaving(true);
    const payload = {
      nombre: form.nombre.trim(), apodo: form.apodo || null, pais_iso: form.pais_iso || null, idiomas: form.idiomas,
      ciudad_vive: { nombre: form.ciudad_vive.nombre || null, pais: form.ciudad_vive.pais || null, lat: null, lng: null },
      cumple: { dia: num(form.cumple.dia), mes: num(form.cumple.mes), anio: num(form.cumple.anio) },
      conocidos: { fecha: form.conocidos.fecha || null, lugar: form.conocidos.lugar || null, como: form.conocidos.como || null },
      estado: form.estado, etiquetas: form.etiquetas,
      notas: Object.fromEntries(Object.entries(form.notas).map(([k, v]) => [k, v || null])),
      telefono: form.telefono || null, redes: form.redes || null, dias_sin_contacto_alerta: num(form.dias_sin_contacto_alerta),
    };
    try {
      const { data } = id ? await api.put(`/people/${id}`, payload) : await api.post("/people", payload);
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/people/${data.id}/foto`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(id ? t("person.saved") : t("person.created"));
      navigate(`/personas/${data.id}`, { replace: true });
    } catch (err) {
      toast.error(errorMessage(err));
    } finally { setSaving(false); }
  };

  return (
    <Shell title={id ? t("person.edit") : t("person.new")} back={id ? `/personas/${id}` : "/personas"} fab={false}>
      <form onSubmit={submit} className="space-y-4 fade-up" data-testid="person-form">
        {(importado || banner) && (
          <p data-testid="import-aviso" className="rounded-xl border border-emerald-500/50 bg-emerald-500/15 p-3 text-sm text-emerald-300 flex items-start gap-2">
            <CheckCircle2 size={18} className="shrink-0 mt-0.5" /> <span>{t("person.importedBanner", { n: importado || banner })}</span>
          </p>
        )}
        {!id && (
          <button type="button" data-testid="import-phone-btn" onClick={importar}
            className="w-full surface p-4 flex items-center gap-4 text-left border-[#d4af37]/40 hover:bg-secondary active:scale-[0.99] transition-colors">
            <span className="w-12 h-12 rounded-full bg-[#d4af37]/15 text-[#f3c649] flex items-center justify-center shrink-0"><Contact size={24} /></span>
            <span className="flex-1 min-w-0">
              <span className="block font-medium">{t("person.importPhone")}</span>
              <span className="block text-sm text-muted-foreground">{t("person.importSub")}</span>
            </span>
            <ChevronRight size={18} className="text-muted-foreground shrink-0" />
          </button>
        )}
        <div className="flex items-center gap-4">
          <button type="button" data-testid="photo-pick-btn" onClick={() => fileRef.current?.click()} className="relative">
            <Avatar nombre={form.nombre} src={preview} size={84} />
            <span className="absolute -bottom-1 -right-1 bg-[#d4af37] text-[#0b0c0e] rounded-full p-1.5"><Camera size={14} /></span>
          </button>
          <input ref={fileRef} data-testid="photo-file-input" type="file" accept="image/*" className="hidden" onChange={onFile} />
          <div className="flex-1 space-y-2">
            <Input testId="input-nombre" placeholder={t("person.name")} value={form.nombre} onChange={(e) => set("nombre", e.target.value)} required />
            <Input testId="input-apodo" placeholder={t("person.nickname")} value={form.apodo} onChange={(e) => set("apodo", e.target.value)} />
          </div>
        </div>

        {!id && (
          <>
            <input ref={vcfRef} data-testid="vcf-file-input" type="file" accept=".vcf,text/vcard,text/x-vcard" className="hidden" onChange={onVcf} />
            <Sheet open={importSheet} onClose={() => setImportSheet(false)} title={t("fab.import")} testId="import-sheet">
              <div className="space-y-4">
                {hasPicker && (
                  <button type="button" data-testid="import-picker-btn" onClick={pickContact} className="btn-primary w-full text-sm">{t("person.pickContacts")}</button>
                )}
                <div className="surface p-3 space-y-2">
                  <p className="text-sm font-medium">{t("person.importStep1")}</p>
                  <ol className="text-xs text-muted-foreground list-decimal pl-4 space-y-1">
                    <li>{t("person.importOpen")} <b>{t("person.importContacts")}</b> {t("person.importAndEnter")}</li><li>{t("person.importTap")} <b>{t("person.importShare")}</b>.</li><li>{t("person.importChoose")} <b>{t("person.importSaveFiles")}</b>.</li><li>{t("person.importBack")}</li>
                  </ol>
                  <button type="button" data-testid="import-vcf-btn" onClick={() => vcfRef.current?.click()} disabled={importing} className="btn-primary text-sm">{importing ? "Importando…" : t("person.importVcfBtn")}</button>
                </div>
                <div className="surface p-3 space-y-2">
                  <p className="text-sm font-medium">{t("person.importStep2")}</p>
                  <p className="text-xs text-muted-foreground">{t("person.importCopilotDesc")}</p>
                  <button type="button" data-testid="import-copiloto-btn" onClick={() => navigate("/", { state: { copiloto: t("person.importPrefix") } })} className="btn-ghost w-full text-sm">{t("person.importGoCopilot")}</button>
                </div>
              </div>
            </Sheet>
          </>
        )}

        <Section title={t("person.origin")}>
          <CountrySelect value={form.pais_iso} onChange={(v) => set("pais_iso", v)} />
          <div>
            <label className="label">{t("person.languages")}</label>
            <div className="flex flex-wrap gap-2">
              {IDIOMAS.map((l) => (
                <button type="button" key={l} data-testid={`idioma-${l}`} onClick={() => set("idiomas", form.idiomas.includes(l) ? form.idiomas.filter((x) => x !== l) : [...form.idiomas, l])}
                  className={`chip ${form.idiomas.includes(l) ? "chip-on" : ""}`}>{idiomaLabel(l)}</button>
              ))}
            </div>
          </div>
        </Section>

        <Section title={t("person.livesIn")}>
          <div className="grid grid-cols-2 gap-3">
            <Input testId="input-ciudad" placeholder={t("person.city")} value={form.ciudad_vive.nombre} onChange={(e) => set("ciudad_vive.nombre", e.target.value)} />
            <Input testId="input-ciudad-pais" placeholder={t("common.country")} value={form.ciudad_vive.pais} onChange={(e) => set("ciudad_vive.pais", e.target.value)} />
          </div>
        </Section>

        <Section title={t("common.birthday")}>
          <div className="grid grid-cols-3 gap-3">
            <Input testId="input-cumple-dia" type="number" min={1} max={31} placeholder={t("person.day")} value={form.cumple.dia} onChange={(e) => set("cumple.dia", e.target.value)} />
            <div>
              <select data-testid="input-cumple-mes" className="field" value={form.cumple.mes} onChange={(e) => set("cumple.mes", e.target.value)}>
                <option value="">{t("person.month")}</option>
                {Array.from({ length: 12 }, (_, i) => <option key={i} value={i + 1}>{mesNombre(i)}</option>)}
              </select>
            </div>
            <Input testId="input-cumple-anio" type="number" min={1900} max={2100} placeholder={t("person.yearOpt")} value={form.cumple.anio} onChange={(e) => set("cumple.anio", e.target.value)} />
          </div>
        </Section>

        <Section title={t("person.howMet")}>
          <Input label={t("common.date")} testId="input-conocidos-fecha" type="date" value={form.conocidos.fecha} onChange={(e) => set("conocidos.fecha", e.target.value)} />
          <Input label={t("person.place")} testId="input-conocidos-lugar" value={form.conocidos.lugar} onChange={(e) => set("conocidos.lugar", e.target.value)} />
          <div>
            <label className="label">{t("person.how")}</label>
            <textarea data-testid="input-conocidos-como" className="field min-h-[72px]" value={form.conocidos.como} onChange={(e) => set("conocidos.como", e.target.value)} />
          </div>
        </Section>

        <Section title={t("person.statusTags")}>
          <div className="flex gap-2">
            {ESTADOS.map((e) => (
              <button type="button" key={e.value} data-testid={`estado-${e.value}`} onClick={() => set("estado", e.value)} className={`chip ${form.estado === e.value ? "chip-on" : ""}`}>{e.label}</button>
            ))}
          </div>
          <div className="flex gap-2">
            <input data-testid="input-etiqueta" className="field" placeholder={t("person.newTag")} value={tag} onChange={(e) => setTag(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }} />
            <button type="button" data-testid="add-etiqueta-btn" onClick={addTag} className="btn-ghost shrink-0">{t("common.add")}</button>
          </div>
          <div className="flex flex-wrap gap-2">
            {form.etiquetas.map((et) => (
              <span key={et} className="chip chip-on" data-testid={`etiqueta-chip-${et}`}>#{et}
                <button type="button" onClick={() => set("etiquetas", form.etiquetas.filter((x) => x !== et))} aria-label={t("common.removeItem")}><X size={12} /></button>
              </span>
            ))}
          </div>
        </Section>

        <Section title={t("common.notesEnc")}>
          {Object.keys(EMPTY.notas).map((k) => (
            <div key={k}>
              <label className="label">{t(`notas.${k}`)}</label>
              <textarea data-testid={`input-nota-${k}`} className="field min-h-[60px]" value={form.notas[k]} onChange={(e) => set(`notas.${k}`, e.target.value)} />
            </div>
          ))}
        </Section>

        <Section title={t("person.contactEnc")}>
          <Input label={t("common.phone")} testId="input-telefono" type="tel" value={form.telefono} onChange={(e) => set("telefono", e.target.value)} />
          <Input label={t("person.social")} testId="input-redes" value={form.redes} onChange={(e) => set("redes", e.target.value)} />
          <Input label={t("person.noContactAlert")} testId="input-dias-alerta" type="number" min={1} value={form.dias_sin_contacto_alerta} onChange={(e) => set("dias_sin_contacto_alerta", e.target.value)} />
        </Section>

        <button data-testid="person-save-btn" className="btn-primary" disabled={saving}>{saving ? t("common.saving") : t("common.save")}</button>
      </form>
    </Shell>
  );
}
